import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import {
  getTeams,
  removeTeamMember,
  updateTeam,
  updateTeamMember,
} from "./teamService";

const TeamEditPage = ({ teamIdOverride, onClose, onSaved }) => {
  const { teamId: teamIdParam } = useParams();
  const api = useApi();
  const { token } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [openMemberId, setOpenMemberId] = useState(null);
  const [draggingMemberId, setDraggingMemberId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [error, setError] = useState("");
  const [memberEdits, setMemberEdits] = useState({});

  const getMemberKey = (member) => member?.publicId || member?.userPublicId;
  const getMemberApiId = (member) => member?.publicId;
  const findMemberByKey = (members, key) =>
    (members || []).find(
      (m) => m.publicId === key || m.userPublicId === key,
    );

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const resolvedTeamId = teamIdOverride || teamIdParam;
      if (!token || !resolvedTeamId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getTeams(api);
        const found = (data || []).find(
          (t) => t.publicId === (teamIdOverride || teamIdParam),
        );
        if (!mounted) return;
        setTeam(found || null);
        setName(found?.name || "");
        setPhotoPreview(found?.pictureUrl || "");
        setPhotoFile(null);
        setError("");
      } catch {
        if (!mounted) return;
        setTeam(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token, teamIdParam, teamIdOverride]);

  const grouped = useMemo(() => {
    const members = (team?.members || []).map((m) => {
      const pending = memberEdits[getMemberKey(m)];
      return pending ? { ...m, ...pending } : m;
    });
    const admins = members.filter((m) => m.isAdmin);
    const anglers = members.filter((m) => m.memberType === "SPORTSMAN");
    const viewers = members.filter((m) => m.memberType === "VIEWER");
    return { admins, anglers, viewers };
  }, [team, memberEdits]);

  const isDirty = useMemo(() => {
    if (!team) return false;
    const memberDirty = Object.keys(memberEdits).length > 0;
    return (
      name.trim() !== (team.name || "").trim() || !!photoFile || memberDirty
    );
  }, [name, photoFile, team, memberEdits]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleMemberMenu = (e, id) => {
    e.stopPropagation();
    setOpenMemberId((current) => (current === id ? null : id));
  };

  const handleRemoveMember = async (e, member) => {
    e.stopPropagation();
    const memberId = getMemberKey(member);
    if (!memberId) return;
    const shouldRemove = window.confirm(
      `Remove ${member.name || member.username || "member"} from this team?`,
    );
    if (!shouldRemove) {
      setOpenMemberId(null);
      return;
    }
    setError("");
    try {
      const apiId = getMemberApiId(member) || memberId;
      try {
        await removeTeamMember(api, apiId);
      } catch (err) {
        if (member?.userPublicId && member.userPublicId !== apiId) {
          await removeTeamMember(api, member.userPublicId);
        } else {
          throw err;
        }
      }
      setTeam((current) => {
        if (!current) return current;
        return {
          ...current,
          members: (current.members || []).filter(
            (m) => getMemberKey(m) !== memberId,
          ),
        };
      });
    } catch {
      setError("Could not remove member. Please try again.");
    } finally {
      setOpenMemberId(null);
    }
  };

  const handleSave = async () => {
    if (!team) return;
    setSaving(true);
    setError("");
    try {
      let updatedTeam = team;
      if (name.trim() !== (team.name || "").trim() || photoFile) {
        const formData = new FormData();
        const payload = { name: name.trim() };
        formData.append(
          "request",
          new Blob([JSON.stringify(payload)], { type: "application/json" }),
        );
        if (photoFile) {
          formData.append("picture", photoFile);
        }
        const updated = await updateTeam(api, team.publicId, formData);
        updatedTeam = updated
          ? { ...team, ...updated }
          : {
              ...team,
              name: name.trim(),
              pictureUrl: photoPreview,
            };
      }

      const editEntries = Object.entries(memberEdits);
      if (editEntries.length > 0) {
        await Promise.all(
          editEntries.map(async ([memberKey, payload]) => {
            const member = findMemberByKey(team?.members, memberKey);
            const apiId =
              getMemberApiId(member) || member?.userPublicId || memberKey;
            try {
              await updateTeamMember(api, apiId, payload);
            } catch (err) {
              if (member?.userPublicId && member.userPublicId !== apiId) {
                await updateTeamMember(api, member.userPublicId, payload);
              } else {
                throw err;
              }
            }
          }),
        );
        updatedTeam = {
          ...updatedTeam,
          members: (updatedTeam.members || []).map((m) =>
            memberEdits[getMemberKey(m)]
              ? { ...m, ...memberEdits[getMemberKey(m)] }
              : m,
          ),
        };
      }

      setTeam(updatedTeam);
      setName(updatedTeam?.name || name);
      setPhotoPreview(updatedTeam?.pictureUrl || photoPreview);
      setPhotoFile(null);
      setMemberEdits({});
      setConfirmSaveOpen(false);
      if (onSaved) {
        onSaved(updatedTeam);
      }
    } catch (err) {
      console.error("Save team failed", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not save team changes. Please try again.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleMemberDrop = (e, target) => {
    e.preventDefault();
    if (!draggingMemberId || !team?.members) return;
    const member = team.members.find(
      (m) => getMemberKey(m) === draggingMemberId,
    );
    if (!member) return;
    const pending = memberEdits[getMemberKey(member)];
    const effectiveMember = pending ? { ...member, ...pending } : member;
    const isAdmin = effectiveMember.isAdmin;
    const memberType = target === "VIEWER" ? "VIEWER" : "SPORTSMAN";
    if (
      effectiveMember.isAdmin === isAdmin &&
      effectiveMember.memberType === memberType
    ) {
      return;
    }
    setMemberEdits((current) => ({
      ...current,
      [getMemberKey(member)]: { memberType, isAdmin },
    }));
    setDraggingMemberId(null);
  };

  const handleDragStart = (e, memberId) => {
    setDraggingMemberId(memberId);
    if (e?.dataTransfer) {
      e.dataTransfer.setData("text/plain", memberId);
    }
  };

  const handleMakeAdmin = (e, member) => {
    e.stopPropagation();
    const memberId = getMemberKey(member);
    if (!memberId) return;
    const shouldPromote = window.confirm(
      `Make ${member.name || member.username || "member"} an admin?`,
    );
    if (!shouldPromote) {
      setOpenMemberId(null);
      return;
    }
    const pending = memberEdits[memberId];
    const effectiveMember = pending ? { ...member, ...pending } : member;
    setMemberEdits((current) => ({
      ...current,
      [memberId]: {
        memberType: effectiveMember.memberType || "SPORTSMAN",
        isAdmin: true,
      },
    }));
    setOpenMemberId(null);
  };

  const handleRemoveAdmin = (e, member) => {
    e.stopPropagation();
    const memberId = getMemberKey(member);
    if (!memberId) return;
    const shouldDemote = window.confirm(
      `Remove admin access for ${member.name || member.username || "member"}?`,
    );
    if (!shouldDemote) {
      setOpenMemberId(null);
      return;
    }
    const pending = memberEdits[memberId];
    const effectiveMember = pending ? { ...member, ...pending } : member;
    const nextType =
      effectiveMember.memberType === "VIEWER" ? "VIEWER" : "SPORTSMAN";
    setMemberEdits((current) => ({
      ...current,
      [memberId]: { memberType: nextType, isAdmin: false },
    }));
    setOpenMemberId(null);
  };

  const allowDrop = (e) => {
    e.preventDefault();
  };

  return (
    <div className="team-edit">
      <div className="team-edit-header">
        <h2>Edit Team</h2>
        <div className="team-edit-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!isDirty || saving}
            onClick={() => setConfirmSaveOpen(true)}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          {onClose && (
            <button
              type="button"
              className="title-modal-close"
              onClick={onClose}
            >
              x
            </button>
          )}
        </div>
      </div>
      {error && <div className="team-edit-error">{error}</div>}
      {loading && <Loader />}
      {!loading && !team && (
        <EmptyState title="Team not found" message="No team data available." />
      )}
      {!loading && team && (
        <div className="team-edit-layout">
          <div className="card team-edit-card">
            <div className="team-edit-photo">
              <img
                src={photoPreview || "/icons/Logo_PNG.png"}
                alt={team.name || "Team"}
              />
              <label className="team-edit-upload">
                Change photo
                <input type="file" accept="image/*" onChange={handlePhotoChange} />
              </label>
              {photoFile && (
                <div className="team-edit-photo-note">New photo selected</div>
              )}
            </div>
            <div className="team-edit-fields">
              <label>
                Team name
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label>
                Invitation code
                <input className="input" value={team.inviteCode || ""} readOnly />
              </label>
            </div>
          </div>

          <div className="card team-edit-members">
            <div className="member-group">
              <div className="member-group-header">
                <h3>Admins</h3>
              </div>
              <div className="member-group-list">
                {grouped.admins.map((m) => (
                  <div key={getMemberKey(m)} className="member-row compact">
                <div className="member-main">
                  <img
                    src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                    alt={m.name || m.username || "Admin"}
                  />
                  <div className="member-info">
                    <div className="member-name">{m.name || m.username}</div>
                    <div className="member-sub">
                      Admin · {m.memberType === "VIEWER" ? "Viewer" : "Angler"}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="member-menu"
                  onClick={(e) => handleMemberMenu(e, getMemberKey(m))}
                >
                  ...
                </button>
                {openMemberId === getMemberKey(m) && (
                  <div className="member-menu-popover">
                    <button
                      type="button"
                      onClick={(e) => handleRemoveAdmin(e, m)}
                    >
                      Remove admin
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleRemoveMember(e, m)}
                    >
                      Remove member
                    </button>
                  </div>
                )}
                  </div>
                ))}
              </div>
            </div>

            <div
              className="member-group"
              onDragOver={allowDrop}
              onDrop={(e) => handleMemberDrop(e, "ANGLER")}
            >
              <div className="member-group-header">
                <h3>Anglers</h3>
                <span className="member-group-hint">
                  Drop here to make angler
                </span>
              </div>
              <div className="member-group-list">
                {grouped.anglers.map((m) => (
                  <div
                    key={getMemberKey(m)}
                    className="member-row compact"
                    draggable
                    onDragStart={(e) => handleDragStart(e, getMemberKey(m))}
                  >
                <div className="member-main">
                  <img
                    src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                    alt={m.name || m.username || "Angler"}
                  />
                  <div className="member-info">
                    <div className="member-name">{m.name || m.username}</div>
                    <div className="member-sub">Angler</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="member-menu"
                  onClick={(e) => handleMemberMenu(e, getMemberKey(m))}
                >
                  ...
                </button>
                {openMemberId === getMemberKey(m) && (
                  <div className="member-menu-popover">
                    {m.isAdmin ? (
                      <button
                        type="button"
                        onClick={(e) => handleRemoveAdmin(e, m)}
                      >
                        Remove admin
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleMakeAdmin(e, m)}
                      >
                        Make admin
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveMember(e, m)}
                    >
                      Remove member
                    </button>
                  </div>
                )}
                  </div>
                ))}
              </div>
            </div>

            <div
              className="member-group"
              onDragOver={allowDrop}
              onDrop={(e) => handleMemberDrop(e, "VIEWER")}
            >
              <div className="member-group-header">
                <h3>Viewers</h3>
                <span className="member-group-hint">
                  Drop here to make viewer
                </span>
              </div>
              <div className="member-group-list">
                {grouped.viewers.map((m) => (
                  <div
                    key={getMemberKey(m)}
                    className="member-row compact"
                    draggable
                    onDragStart={(e) => handleDragStart(e, getMemberKey(m))}
                  >
                <div className="member-main">
                  <img
                    src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                    alt={m.name || m.username || "Viewer"}
                  />
                  <div className="member-info">
                    <div className="member-name">{m.name || m.username}</div>
                    <div className="member-sub">Viewer</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="member-menu"
                  onClick={(e) => handleMemberMenu(e, getMemberKey(m))}
                >
                  ...
                </button>
                {openMemberId === getMemberKey(m) && (
                  <div className="member-menu-popover">
                    {m.isAdmin ? (
                      <button
                        type="button"
                        onClick={(e) => handleRemoveAdmin(e, m)}
                      >
                        Remove admin
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleMakeAdmin(e, m)}
                      >
                        Make admin
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => handleRemoveMember(e, m)}
                    >
                      Remove member
                    </button>
                  </div>
                )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmSaveOpen && (
        <div
          className="title-modal-backdrop"
        >
          <div className="title-modal" onClick={(e) => e.stopPropagation()}>
            <div className="title-modal-header">
              <div className="title-modal-title">Confirm Save</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setConfirmSaveOpen(false)}
              >
                x
              </button>
            </div>
            <div className="title-modal-body">
              <div>Are you sure you want to edit this team?</div>
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmSaveOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Yes, Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamEditPage;

